<template>
  <div>
    <menu-pages-navbar class="mb-3" />
    <div
      class="grow-1 mt-4"
      :class="[$vuetify.breakpoint.smAndUp ? 'mr-4' : 'mx-0']"
    >
      <!-- Loading Indicator -->
      <div class="loading-indicator">
        <v-progress-circular
          v-if="loading"
          :size="50"
          indeterminate
          class="mt-5 msr--text"
          style="position: absolute; pointer-events: none"
        />
      </div>
      <!-- Top: Create/Edit MenuItem UI -->
      <div>
        <v-layout
          align-center
          wrap
        >
          <!-- Header -->
          <v-layout
            align-center
            class="title"
          >
            <h3>{{ $t($route.name) }}</h3>
          </v-layout>
          <v-layout
            align-center
            justify-end
            nowrap
            :class="{'mt-3': $vuetify.breakpoint.smAndDown}"
          >
            <!-- Abort button -->
            <v-btn
              round
              flat
              class="grey lighten-4"
              :title="$t('goBack')"
              @click="goBack()"
            >
              {{ $t('goBack') }}
            </v-btn>
            <!-- Confirm creation/update button -->
            <v-btn
              data-cy="create-edit-menu-item-page-confirm-btn"
              round
              :dark="formIsValid"
              color="msr"
              :disabled="!formIsValid"
              @click="save()"
            >
              <span v-if="inCreateMode">{{ $t('createmenuitem') }}</span>
              <span v-else-if="inEditMode">{{ $t('save') }}</span>
            </v-btn>
          </v-layout>
        </v-layout>
        <!-- Two column layout -->
        <v-layout
          align-start
          wrap
          class="mt-4"
        >
          <!-- Left column -->
          <v-flex
            xs12
            sm6
            :class="{'pr-3': $vuetify.breakpoint.smAndUp}"
          >
            <!-- Category -->
            <v-select
              v-model="payload.category"
              :label="$t('menuCategory')"
              item-text="name"
              item-value="id"
              return-object
              :items="orderedMenuCategories"
              prepend-icon="menu_book"
            />
            <v-layout v-bind="layout">
              <!-- Name -->
              <v-form
                ref="name-form"
                class="grow-3"
                :class="{'mr-3': $vuetify.breakpoint.smAndUp}"
                @submit.prevent
              >
                <v-text-field
                  ref="nameInput"
                  v-model="payload.name"
                  data-cy="create-edit-menu-item-page-name-input"
                  :label="$t('name')"
                  counter="128"
                  @keyup.enter="save()"
                />
              </v-form>
              <!-- Additives -->
              <additives-select
                :menu-id="menuId"
                :additives="orderedMenuAdditives"
                :selection.sync="payload.additives"
                @additive-created="
                  () => $store.dispatch('LIST_MENU_ADDITIVES', menuId)
                "
              />
            </v-layout>
            <!-- Description -->
            <v-textarea
              v-if="menu.show_menu_item_description"
              v-model="payload.description"
              :label="$t('description')"
              auto-grow
              rows="2"
              clearable
            />
            <!-- Variants -->
            <div class="pt-1 mb-4">
              <edit-menu-item-variants
                ref="variants"
                v-model="payload.variants"
                :menu-additives="orderedMenuAdditives"
                :menu-id="menuId"
                :allow-price-modifiers="menu.show_menu_item_price_modifiers"
                :allow-extended="menu.show_menu_item_extended_variants"
                :allow-multiple="menu.show_menu_item_multiple_variants"
              />
            </div>
            <!-- Beverage Deposit -->
            <div
              v-if="menu.show_menu_item_has_beverage_deposit"
              class="mb-1"
            >
              <v-select
                v-model="payload.beverage_deposit"
                item-value="id"
                :items="menuBeverageDeposits"
                :label="$t('menuItemBeverageDepositShort')"
                clearable
              />
            </div>
            <!-- Is new -->
            <div
              v-if="menu.show_menu_item_is_new"
              class="mb-1"
            >
              <v-checkbox
                v-model="payload.is_new"
                :label="$t('menuItemIsNewTooltip')"
                hide-details
                color="msr"
                class="ma-0"
              />
            </div>
            <!-- Veggie & Co -->
            <v-layout
              v-if="menu.show_menu_item_dietary_choices"
              align-center
              class="mt-1 pb-3"
            >
              <v-select
                v-model="dietaryChoices"
                :items="dietaryOptions"
                item-value="value"
                hide-details
                clearable
                multiple
                :placeholder="$t('menuItemIsNoSpecificDiet')"
              >
                <template
                  slot="item"
                  slot-scope="data"
                >
                  <v-list-tile-avatar
                    :style="{opacity: data.item.disabled ? 0.33 : 1}"
                  >
                    <v-checkbox
                      color="msr"
                      :input-value="dietaryChoices.includes(data.item.value)"
                      class="mr-2"
                    />
                  </v-list-tile-avatar>
                  <v-list-tile-avatar
                    v-if="data.item.icon"
                    :style="{opacity: data.item.disabled ? 0.5 : 1}"
                  >
                    <img
                      :src="data.item.icon"
                      style="border-radius: 0; width: 32px; height: 32px"
                    >
                  </v-list-tile-avatar>
                  <v-list-tile-content
                    :style="{opacity: data.item.disabled ? 0.5 : 1}"
                  >
                    <v-list-tile-title>
                      {{ data.item.text }}
                    </v-list-tile-title>
                  </v-list-tile-content>
                </template>
                <template
                  slot="selection"
                  slot-scope="data"
                >
                  <v-list-tile-avatar
                    v-if="data.item.icon && !data.item.disabled"
                  >
                    <img
                      :src="data.item.icon"
                      style="border-radius: 0; width: 32px; height: 32px"
                    >
                  </v-list-tile-avatar>
                  <v-list-tile-content v-if="!data.item.disabled">
                    <v-list-tile-title>
                      {{ data.item.text }}
                    </v-list-tile-title>
                  </v-list-tile-content>
                </template>
              </v-select>
            </v-layout>
            <!-- Hard-Range -->
            <v-layout
              align-center
              :wrap="$vuetify.breakpoint.smAndDown"
            >
              <!-- Hard Start -->
              <v-menu
                lazy
                offset-y
                :nudge-left="40"
              >
                <v-text-field
                  slot="activator"
                  prepend-icon="event"
                  readonly
                  :value="payload.hard_start"
                  :label="
                    !!payload.hard_start || !!payload.hard_expire
                      ? $t('from')
                      : $t('menuItemHardRange')
                  "
                  clearable
                  :class="{'mr-3': $vuetify.breakpoint.mdAndUp}"
                  @click:clear="payload.hard_start = null"
                />
                <v-date-picker
                  v-model="payload.hard_start"
                  color="msr"
                  scrollable
                  event-color="msr"
                  :no-title="$vuetify.breakpoint.xsOnly"
                  :first-day-of-week="1"
                  :locale="$t('_datepickerLocale')"
                />
              </v-menu>
              <!-- Hard Expire -->
              <v-menu
                lazy
                offset-y
                :nudge-left="40"
              >
                <v-text-field
                  slot="activator"
                  prepend-icon="event"
                  readonly
                  :value="payload.hard_expire"
                  :label="
                    !!payload.hard_start || !!payload.hard_expire
                      ? $t('toIncluding')
                      : ''
                  "
                  clearable
                  @click:clear="payload.hard_expire = null"
                />
                <v-date-picker
                  v-model="payload.hard_expire"
                  color="msr"
                  scrollable
                  event-color="msr"
                  :no-title="$vuetify.breakpoint.xsOnly"
                  :first-day-of-week="1"
                  :locale="$t('_datepickerLocale')"
                />
              </v-menu>
            </v-layout>
            <v-slide-y-transition>
              <v-layout
                v-if="
                  (lastHardStart || lastHardExpire) &&
                    (payload.hard_start != lastHardStart ||
                      payload.hard_expire != lastHardExpire)
                "
                align-baseline
              >
                <span
                  class="mr-2 grey--text text--darken-2"
                >{{ $t('setTimingFromRemembered') }}:</span>
                <v-chip
                  color="msr"
                  small
                  close
                  outline
                  class="clickable mr-0 mb-0"
                  :title="$t('setTimingFromRemembered')"
                  @click="assignLastHardRangeValues()"
                  @input="removeLastHardRangeValues()"
                >
                  <span v-if="lastHardStart">
                    <span v-if="!lastHardExpire">{{ $t('from') }}:</span>
                    {{ lastHardStart | dayjs('DD.MM.YYYY') }}
                  </span>
                  <span
                    v-if="lastHardStart && lastHardExpire"
                  >&nbsp;-&nbsp;</span>
                  <span v-if="lastHardExpire">
                    <span v-if="!lastHardStart">{{ $t('to') }}:</span>
                    {{ lastHardExpire | dayjs('DD.MM.YYYY') }}
                  </span>
                </v-chip>
              </v-layout>
            </v-slide-y-transition>
          </v-flex>
          <!-- Right column -->
          <v-flex
            xs12
            sm6
            :class="{'pl-3': $vuetify.breakpoint.smAndUp}"
          >
            <!-- Image -->
            <div
              v-if="menu.show_menu_item_image"
              class="mt-4"
            >
              <v-layout
                class="menu-item-image-box mt-3 unselectable"
                :class="{
                  'has-image': !!imageUrl,
                  border: !imageUrl,
                  dragging: imageBeingDragged,
                  clickable: !imageUrl,
                }"
                style="max-height: 640px"
                :style="{
                  'aspect-ratio': !imageUrl
                    ? (menu.menu_item_image_width || 790) /
                      (menu.menu_item_image_height || 240)
                    : null,
                }"
                column
                align-center
                justify-center
                @click="!imageUrl ? onImageUploadButtonClicked() : null"
              >
                <v-layout
                  v-if="imageUrl"
                  column
                  class="full-width"
                >
                  <v-img
                    ref="poster"
                    contain
                    class="clickable v-image--left"
                    :src="imageUrl"
                    max-height="640"
                    @click="onImageUploadButtonClicked()"
                  />
                  <a
                    href="javascript:void(0)"
                    class="caption grey--text text--darken-1 mt-2"
                    @click.stop="askClearImage()"
                  >{{ $t('clearImage') }}</a>
                </v-layout>
                <v-layout
                  v-else
                  ref="dnd-file-area"
                  column
                  justify-center
                  class="pa-4 full-width ignore-mouse-events-on-child-elements"
                >
                  <v-icon x-large>
                    insert_photo
                  </v-icon>
                  <div
                    class="mt-2 body-2 grey--text text--darken-2 text-xs-center"
                  >
                    {{ $t('createNewImageUpload') }}<br>
                    <span
                      v-if="
                        menu &&
                          menu.menu_item_image_width &&
                          menu.menu_item_image_height
                      "
                      class="caption grey--text"
                    >
                      {{ $t('createNewImageRecommendation') }}:
                      {{ menu.menu_item_image_width }} x
                      {{ menu.menu_item_image_height }} px
                    </span>
                  </div>
                </v-layout>
              </v-layout>
            </div>
          </v-flex>
        </v-layout>
      </div>
      <!-- Bottom: Stores for edited MenuItem UI -->
      <v-slide-y-transition>
        <div v-if="!!editedMenuItem">
          <div
            ref="storesheader"
            class="mt-5 unselectable white py-2 px-3 clickable"
            style="
              scroll-margin-top: 56px;
              width: fit-content;
              display: flex;
              align-items: center;
            "
            :title="expandStores ? $t('clickToCollapse') : $t('clickToExpand')"
            data-cy="toggle-menu-items-expansion"
            @click="expandStores = !expandStores"
          >
            <v-layout
              class="grey--text text--darken-2 mr-2"
              align-center
            >
              <v-icon
                class="mr-3"
                medium
                color="grey"
              >
                location_on
              </v-icon>
              <div>
                <div class="title">
                  {{ $t('menuitemstores') }}
                </div>
                <div class="caption mt-1">
                  <span
                    v-if="activeStores.length > 0"
                    class="success--text"
                    style="display: inline-block"
                  >
                    {{ $t('menuItemStateActive') }}
                    {{ activeStores.length }} </span><span
                      v-if="
                        activeStores.length > 0 && deactivatedStores.length > 0
                      "
                    >,</span>
                  <span
                    v-if="deactivatedStores.length > 0"
                    class="error--text"
                    style="display: inline-block"
                  >
                    {{ $t('menuItemStateSoldOut') }}
                    {{ deactivatedStores.length }}
                  </span>
                </div>
              </div>
            </v-layout>
            <div
              v-if="loading || loadingStores"
              style="
                width: 36px;
                height: 36px;
                display: flex;
                justify-content: center;
                align-items: center;
              "
            >
              <v-progress-circular
                color="msr"
                size="20"
                width="2"
                indeterminate
              />
            </div>
            <v-icon
              v-else
              large
              :style="{
                transform: expandStores ? 'rotate(180deg)' : '',
              }"
            >
              expand_more
            </v-icon>
          </div>
          <v-slide-y-transition>
            <div
              v-if="expandStores"
              class="mt-4"
            >
              <!-- Disabled Warning -->
              <div
                v-if="!editedMenuItem.is_enabled"
                class="mt-3"
              >
                <v-layout align-center>
                  <v-icon
                    color="error"
                    class="mr-3"
                    medium
                  >
                    visibility_off
                  </v-icon>
                  <div class="subheading">
                    {{ $t('menuItemIsDisabledRightNow') }}.
                    <div
                      class="pseudo-anchor msr--text"
                      @click="confirmEnableEditedMenuItem()"
                    >
                      {{ $t('menuItemEnableAgain') }}
                    </div>
                  </div>
                </v-layout>
              </div>
              <!-- Search -->
              <v-text-field
                v-if="stores.length > 5 && editedMenuItem.is_enabled"
                v-model="storesSearchText"
                class="mt-3"
                :label="storesSearchLabel"
                clearable
                append-icon="search"
                style="max-width: 300px"
                @click:clear="clearStoresSearch()"
                @keyup.native.esc="clearStoresSearch()"
              />
              <!-- Stores Table -->
              <v-data-table
                v-if="editedMenuItem.is_enabled && !loading && !loadingStores"
                hide-actions
                disable-initial-sort
                :headers="storeHeaders"
                :items="filteredStores"
                :no-data-text="$t('menuItemStoresNone')"
                :no-results-text="$t('menuItemStoresNone')"
                class="mt-3"
              >
                <template
                  slot="items"
                  slot-scope="props"
                >
                  <tr
                    :key="props.item.id"
                    class="store-row"
                    :class="{
                      'bg-error':
                        !props.item.menu_item_active ||
                        !!props.item.deactivated_until,
                    }"
                  >
                    <!-- Active in Store -->
                    <td
                      width="10%"
                      :style="{
                        'min-width': $vuetify.breakpoint.lgAndUp
                          ? '345px'
                          : 'initial',
                      }"
                    >
                      <v-menu
                        lazy
                        style="width: 100%"
                      >
                        <v-btn
                          slot="activator"
                          flat
                          round
                          class="ma-0"
                          :title="$t('menuItemChangeState')"
                        >
                          <!-- State: Active -->
                          <span
                            v-if="props.item.menu_item_active"
                            class="success--text"
                          >
                            <v-icon>check</v-icon>
                          </span>
                          <!-- State: Sold out -->
                          <span
                            v-else-if="!props.item.deactivated_until"
                            class="error--text"
                          >{{ $t('menuItemStateSoldOut') }}</span>
                          <!-- State: Sold out until date -->
                          <span
                            v-else
                            class="error--text"
                          >{{ $t('menuItemStateSoldOutUntil') }}
                            {{
                              subtractOneDayFromDate(
                                props.item.deactivated_until
                              ) | dayjs('DD.MM.YYYY')
                            }}
                          </span>
                          <!-- Dropdown Icon -->
                          <v-icon
                            class="btn-state--dropdown-icon ml-1"
                            medium
                          >
                            arrow_drop_down
                          </v-icon>
                        </v-btn>
                        <v-list>
                          <!-- Set active -->
                          <v-list-tile
                            v-if="!props.item.menu_item_active"
                            @click="activateInStore(props.item)"
                          >
                            {{ $t('menuItemStateActive') }}
                          </v-list-tile>
                          <v-divider v-if="!props.item.menu_item_active" />
                          <!-- Set sold-out -->
                          <v-list-tile @click="deactivateInStore(props.item)">
                            {{ $t('menuItemStateSoldOut') }}
                          </v-list-tile>
                          <!-- Set sold-out until tomorrow -->
                          <v-list-tile
                            @click="deactivateInStoreUntilTomorrow(props.item)"
                          >
                            {{ $t('menuItemStateSoldOutUntilTomorrow') }}
                          </v-list-tile>
                          <!-- Set sold-out until specific date -->
                          <v-list-tile
                            @click="openDatePickerForStore($event, props.item)"
                          >
                            {{ $t('menuItemStateSoldOutUntilDate') }}
                          </v-list-tile>
                        </v-list>
                      </v-menu>
                    </td>
                    <!-- City/Name -->
                    <td>
                      <v-layout
                        class="body-1"
                        align-center
                      >
                        <v-icon
                          medium
                          class="msr--text mr-3 unselectable"
                        >
                          location_on
                        </v-icon>
                        <div
                          class="store-menu-link pseudo-anchor--inverted"
                          :title="$t('menuTVDecoderStoreMenuLink')"
                          @click="goToStore(props.item)"
                        >
                          <small class="d-block grey--text text--darken-2">{{
                            props.item.name
                          }}</small>
                          <strong>{{ props.item.city }}</strong>
                        </div>
                      </v-layout>
                    </td>
                  </tr>
                </template>
              </v-data-table>
            </div>
          </v-slide-y-transition>
        </div>
      </v-slide-y-transition>
    </div>
    <!-- Scroll Spacer -->
    <div style="min-height: 60px" />
    <!-- Image Upload -->
    <image-replacement
      ref="imagereplacement"
      @cropped="onImageChangeConfirmed"
    />
    <!-- Date Picker for deactivated_until -->
    <v-menu
      v-model="showDeactivationUntilMenu"
      :close-on-content-click="false"
      lazy
      min-width="290px"
      absolute
      :position-x="deactivationUntilMenuPositionX"
      :position-y="deactivationUntilMenuPositionY"
    >
      <v-date-picker
        v-model="deactivationUntilDate"
        color="msr"
        scrollable
        :allowed-dates="isNotInPast"
      >
        <v-layout column>
          <div
            v-if="deactivationUntilStore"
            class="mx-3 mb-3"
          >
            <div class="mb-2">
              <small class="grey--text text--darken-2">{{
                deactivationUntilStore.name
              }}</small>
              <div
                class="font-weight-bold"
                style="font-size: medium"
              >
                {{ deactivationUntilStore.city }}
              </div>
            </div>
            <span v-if="!deactivationUntilDate">{{
              $t('menuItemStateSoldOutUntilDate')
            }}</span>
            <span
              v-else
            >{{ $t('menuItemStateSoldOutUntil') }}
              {{ deactivationUntilDate | dayjs('DD.MM.YYYY') }}</span>
          </div>
          <v-layout>
            <v-spacer />
            <v-btn
              flat
              round
              color="black"
              @click="onDeactivationUntilDateCancelled()"
            >
              {{ $t('close') }}
            </v-btn>
            <v-btn
              :disabled="!deactivationUntilDate"
              round
              color="msr"
              class="white--text"
              @click="
                onDeactivationUntilDateChosen(
                  deactivationUntilStore,
                  deactivationUntilDate
                )
              "
            >
              {{ $t('save') }}
            </v-btn>
            <v-spacer />
          </v-layout>
        </v-layout>
      </v-date-picker>
    </v-menu>
  </div>
</template>

<script>
import {mapState} from 'vuex';
import dayjs from 'dayjs';
import debounce from 'lodash.debounce';
import orderBy from 'lodash.orderby';

import {removeQueryParamFromCurrentRoute} from '@/utils';
import matchesFilter from '@/matchesFilter';

import {isBefore, subtractOneDayFromDate} from '@/mixins/timing.js';
import timing from '@/mixins/timing.js';
import activateMenuItem from '@/mixins/activateMenuItem.js';
import menuBeverageDeposits from '@/mixins/menuBeverageDeposits.js';

import {
  SYMBOL_VEGAN_URL,
  SYMBOL_VEGETARIAN_URL,
  SYMBOL_GLUTEN_FREE_URL,
  SYMBOL_LACTOSE_FREE_URL,
} from '@/pages/MenuItems';

import AdditivesSelect from '@/components/Menu/AdditivesSelect';
import EditMenuItemVariants from '@/components/Menu/EditMenuItemVariants';
import ImageReplacement from '@/components/ImageReplacement';
import MenuPagesNavbar from '@/components/Menu/MenuPagesNavbar';

const GLUTEN_FREE = 'gluten_free';
const LACTOSE_FREE = 'lactose_free';
const VEGAN = 'vegan';
const VEGETARIAN = 'vegetarian';

/**
 * Avoid reusing state that may been modified.
 */
function createDefaultPayload() {
  return {
    additives: [], // IDs
    beverage_deposit: null, // ID
    category: null, // ID
    description: '',
    hard_expire: null,
    hard_start: null,
    image: null,
    is_gluten_free: false,
    is_lactose_free: false,
    is_new: false,
    is_vegan: false,
    is_vegetarian: false,
    name: '',
    variants: [
      {
        additives: [],
        amount: null,
        description: '',
        extended: false,
        modifier: '=',
        price: null,
      },
    ],
  };
}

export default {
  components: {
    AdditivesSelect,
    EditMenuItemVariants,
    ImageReplacement,
    MenuPagesNavbar,
  },
  mixins: [activateMenuItem, menuBeverageDeposits, timing],
  data() {
    return {
      loading: false,
      loadingStores: false,

      imageEventHandlers: {},
      imageBeingDragged: false,

      lastHardStartKey: 'edit-menuitem:last-hard-start',
      lastHardStart: null,
      lastHardExpireKey: 'edit-menuitem:last-hard-expire',
      lastHardExpire: null,

      dietaryChoices: [],
      payload: createDefaultPayload(),

      storesSearchText: '',

      // Note: See StoreMenu.vue for this functionality from the
      //   'opposite side'.
      showDeactivationUntilMenu: null,
      deactivationUntilStore: null,
      deactivationUntilMenuPositionX: 0,
      deactivationUntilMenuPositionY: 0,
      deactivationUntilDate: null,

      storeHeaders: [
        {
          text: this.$t('menuItemActiveInStore'),
          value: 'menu_item_active',
          sortable: true,
        },
        {
          text: this.$t('store'),
          value: 'store_key',
          sortable: true,
        },
      ],

      // From backend.
      menu: {},
      stores: [],
    };
  },
  computed: {
    ...mapState({
      menuAdditives: (state) => state.common.menuAdditives,
      menuCategories: (state) => state.common.menuCategories,
      menuItems: (state) => state.common.menuItems,
    }),
    // Note: Reusing an existing store attribute here that saves the
    //   expansion state of the inverted table (MenuItems for Store) of
    //   the one we are showing here (Stores for MenuItem): The use case
    //   is the same so it's okay, imho.
    expandStores: {
      get() {
        return this.$store.state.common.storeMenuExpandMenuItems;
      },
      set(expand) {
        this.$store.commit('SET_STORE_MENU_EXPAND_MENU_ITEMS', expand);
      },
    },
    activeStores() {
      return this.stores.filter((store) => {
        return store.menu_item_active;
      });
    },
    deactivatedStores() {
      return this.stores.filter((store) => {
        return !store.menu_item_active;
      });
    },
    rules() {
      return {
        uniqueAdditiveName: (v) =>
          this.isAdditiveNameUnique(v) || this.$t('nameAlreadyInUse'),
      };
    },
    newAdditiveNameIsValid() {
      return (
        !!this.newAdditiveName &&
        this.newAdditiveName.trim() != '' &&
        this.isAdditiveNameUnique(this.newAdditiveName)
      );
    },
    augmentedStores() {
      return this.stores.map((store) => {
        store.store_key = store.city + store.name;
        return store;
      });
    },
    /**
     * Match Stores against comma-separated search terms.
     */
    filteredStores() {
      if (!this.storesSearchText) {
        return this.augmentedStores;
      }

      const matchingStores = [];
      const words = this.storesSearchText.split(',');

      for (const store of this.augmentedStores) {
        for (let word of words) {
          word = word.trim();
          if (
            matchesFilter(store.city, word) ||
            matchesFilter(store.name, word)
          ) {
            if (matchingStores.indexOf(store) != -1) {
              // Already matched before, avoid duplicate matching.
              continue;
            }
            matchingStores.push(store);
            break;
          }
        }
      }
      return matchingStores;
    },
    storesSearchLabel() {
      const prefix = this.$t('search');
      if (!this.storesSearchText) {
        return `${prefix} (${this.augmentedStores.length} ${this.$t(
          'stores'
        )})`;
      }
      return `${prefix} (${this.filteredStores.length}/${
        this.augmentedStores.length
      } ${this.$t('stores')})`;
    },
    layout() {
      if (this.$vuetify.breakpoint.xsOnly) {
        return {column: true};
      }
      return {row: true};
    },
    /**
     * Handle image being either a URL String or a cropped image Blob.
     */
    imageUrl() {
      if (this.payload.image === null) {
        return null;
      }
      if (this.payload.image instanceof Blob) {
        return URL.createObjectURL(this.payload.image);
      }
      // It's a URL already.
      return this.payload.image;
    },
    dietaryOptions() {
      const disableVegetarianOption = this.dietaryChoices.includes(VEGAN);
      return [
        {
          value: VEGETARIAN,
          text: this.$t('menuItemIsVegetarian'),
          icon: SYMBOL_VEGETARIAN_URL,
          disabled: disableVegetarianOption,
        },
        {
          value: VEGAN,
          text: this.$t('menuItemIsVegan'),
          icon: SYMBOL_VEGAN_URL,
        },
        {
          value: GLUTEN_FREE,
          text: this.$t('menuItemIsGlutenFree'),
          icon: SYMBOL_GLUTEN_FREE_URL,
        },
        {
          value: LACTOSE_FREE,
          text: this.$t('menuItemIsLactoseFree'),
          icon: SYMBOL_LACTOSE_FREE_URL,
        },
      ];
    },
    /**
     * The ID either comes from route params or from query params.
     */
    menuId() {
      if ((this.$route.query || {}).menu) {
        return this.$route.query.menu;
      }
      return this.$route.params.menuId;
    },
    /**
     * Select optional MenuItem to edit via route parameter.
     */
    editedMenuItem() {
      if (!this.$route.params.menuItemId) {
        return null;
      }
      const menuItem = this.menuItems.filter(
        (item) => item.id == this.$route.params.menuItemId
      )[0];
      return menuItem;
    },
    inEditMode() {
      return !!this.editedMenuItem;
    },
    inCreateMode() {
      return !this.inEditMode;
    },
    otherMenuItems() {
      if (this.inEditMode) {
        return this.menuItems.filter(
          (item) => item.id != this.editedMenuItem.id
        );
      }
      return this.menuItems;
    },
    orderedMenuCategories() {
      return orderBy(this.menuCategories, 'name');
    },
    orderedMenuAdditives() {
      return orderBy(this.menuAdditives, 'name');
    },
    formIsValid() {
      return (
        // Name not empty
        !!this.payload.name &&
        this.payload.name.trim() !== '' &&
        // Category
        !!this.payload.category &&
        // Variants
        this.variantsValid
      );
    },
    /**
     * - Need minimum of one Variant.
     * - If we have just one Variant, amount is optional.
     * - Each Variant needs a valid price value.
     * - If we have multiple Variant, each need a unique amount (alternatively: description) value.
     */
    variantsValid() {
      const numVariants = this.payload.variants.length;
      if (numVariants == 0) {
        console.debug('[ERROR] Need at least one Variant');
        return false;
      }
      if (numVariants > 1) {
        // Check no duplicates (one amount can be an empty string though).
        const uniqueAmountsOrDescriptions = new Set(
          this.payload.variants.map((variant) => {
            const key = (
              (variant.amount || '') +
              ' ' +
              (variant.description || '')
            ).trim();
            return key;
          })
        );
        if (uniqueAmountsOrDescriptions.size != numVariants) {
          console.debug(
            '[ERROR] Variants all need a unique amount (or description)'
          );
          return false;
        }
      }

      for (const variant of this.payload.variants) {
        if (!variant.price) {
          console.debug('[ERROR] Variant has an invalid price value', variant);
          return false;
        }
      }

      console.debug('[INFO] Variants valid');
      return true;
    },
  },
  watch: {
    'payload.name': function () {
      this.validateNameForm();
    },
    'payload.category': function () {
      this.validateNameForm();
    },
    /** Unset hard_expire when new hard_start invalidates it. */
    'payload.hard_start': function () {
      if (this.payload.hard_start && this.payload.hard_expire) {
        if (isBefore(this.payload.hard_expire, this.payload.hard_start)) {
          this.payload.hard_expire = null;
        }
      }
    },
    /** Unset hard_start when new hard_expire invalidates it. */
    'payload.hard_expire': function () {
      if (this.payload.hard_start && this.payload.hard_expire) {
        if (isBefore(this.payload.hard_expire, this.payload.hard_start)) {
          this.payload.hard_start = null;
        }
      }
    },
    dietaryChoices: function (choices) {
      // Auto-select vegetarian if vegan is selected.
      if (choices.includes(VEGAN) && !choices.includes(VEGETARIAN)) {
        choices.push(VEGETARIAN);
      }

      // Persist choices into payload, reset payload if none are selected.
      if (!choices) {
        this.payload.is_vegetarian = false;
        this.payload.is_vegan = false;
        this.payload.is_gluten_free = false;
        this.payload.is_lactose_free = false;
      }

      if (choices.includes(VEGETARIAN)) {
        this.payload.is_vegetarian = true;
      } else {
        this.payload.is_vegetarian = false;
      }

      if (choices.includes(VEGAN)) {
        this.payload.is_vegetarian = true;
        this.payload.is_vegan = true;
      } else {
        this.payload.is_vegan = false;
      }

      if (choices.includes(GLUTEN_FREE)) {
        this.payload.is_gluten_free = true;
      } else {
        this.payload.is_gluten_free = false;
      }

      if (choices.includes(LACTOSE_FREE)) {
        this.payload.is_lactose_free = true;
      } else {
        this.payload.is_lactose_free = false;
      }
    },
    /**
     * Handle expansion and scrolling if specified in URL query params.
     */
    stores: function () {
      if (this.stores.length == 0) {
        return;
      }

      if (this.$route.query && this.$route.query.expand_stores) {
        this.expandStores = true;
        removeQueryParamFromCurrentRoute('expand_stores');

        setTimeout(() => {
          this.$refs.storesheader.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }, 100);
      }
    },
  },
  created() {
    // We may have menuItems already, but will try again after they are refetched.
    this.initializePayloadFromEditedMenuItem();

    this.fetchData().then(() => {
      this.initializePayloadFromEditedMenuItem();
      this.$nextTick(() => {
        this.registerImageDragAndDropEvents();
        this.$refs.nameInput.focus();
      });
      this.loadLastHardRangeValues();
    });
  },
  beforeDestroy() {
    this.deregisterImageDragAndDropEvents();
  },
  methods: {
    rememberLastHardRangeValues() {
      if (this.payload.hard_start == null && this.payload.hard_expire == null) {
        return;
      }
      window.sessionStorage.setItem(
        this.lastHardStartKey,
        this.payload.hard_start || ''
      );
      window.sessionStorage.setItem(
        this.lastHardExpireKey,
        this.payload.hard_expire || ''
      );
    },
    loadLastHardRangeValues() {
      this.lastHardStart =
        window.sessionStorage.getItem(this.lastHardStartKey) || null;
      this.lastHardExpire =
        window.sessionStorage.getItem(this.lastHardExpireKey) || null;
    },
    assignLastHardRangeValues() {
      this.payload.hard_start = this.lastHardStart;
      this.payload.hard_expire = this.lastHardExpire;
    },
    removeLastHardRangeValues() {
      window.sessionStorage.removeItem(this.lastHardStartKey);
      window.sessionStorage.removeItem(this.lastHardExpireKey);
      this.loadLastHardRangeValues();
    },
    askClearImage() {
      const confirmed = confirm(this.$t('menuItemConfirmImageRemoval'));
      if (!confirmed) {
        return;
      }
      this.clearImage();
    },
    clearImage() {
      this.payload.image = null;

      // As the associated UI was not in DOM before, register drag events again.
      this.$nextTick(() => {
        this.registerImageDragAndDropEvents();
      });
    },
    getImageDragAndDropArea() {
      return this.$refs['dnd-file-area'];
    },
    registerImageDragAndDropEvents() {
      const area = this.getImageDragAndDropArea();
      if (!area) {
        return;
      }

      this.deregisterImageDragAndDropEvents();

      const vm = this;

      function onDragEnter(e) {
        vm.imageBeingDragged = true;
      }

      function onDragLeave(e) {
        e.preventDefault();
        vm.imageBeingDragged = false;
      }

      function onDragOver(e) {
        e.preventDefault();
      }

      function onDrop(e) {
        e.preventDefault();
        vm.imageBeingDragged = false;

        const droppedImageFile = e.dataTransfer.files[0];
        vm.onImageUploadButtonClicked(droppedImageFile);
      }

      this.imageEventHandlers = {};
      this.imageEventHandlers.dragenter = onDragEnter;
      this.imageEventHandlers.dragleave = onDragLeave;
      this.imageEventHandlers.dragover = onDragOver;
      this.imageEventHandlers.drop = onDrop;

      area.addEventListener('dragenter', this.imageEventHandlers.dragenter);
      area.addEventListener('dragleave', this.imageEventHandlers.dragleave);
      area.addEventListener('dragover', this.imageEventHandlers.dragover);
      area.addEventListener('drop', this.imageEventHandlers.drop);

      console.log('[DEBUG] DnD Events registered');
    },
    deregisterImageDragAndDropEvents() {
      if (!this.imageEventHandlers) {
        return;
      }

      const area = this.getImageDragAndDropArea();
      if (!area) {
        return;
      }

      area.removeEventListener('dragenter', this.imageEventHandlers.dragenter);
      area.removeEventListener('dragleave', this.imageEventHandlers.dragleave);
      area.removeEventListener('dragover', this.imageEventHandlers.dragover);
      area.removeEventListener('drop', this.imageEventHandlers.drop);

      this.imageEventHandlers = {};
    },
    onImageUploadButtonClicked(droppedFile = null) {
      const opts = {
        filename: null,
        field: {
          name: 'image',
          data: {
            width: 2048,
            height: 2048,
            crop_ratio: 'any',
          },
          value: null,
        },
      };

      if (droppedFile) {
        opts.filename = droppedFile.name;
        opts.recrop = true;
        opts.value = URL.createObjectURL(droppedFile);
      }

      if (this.inEditMode) {
        opts.field.data.crop_ratio = this.menu.menu_item_image_crop_ratio;
        opts.field.data.width = this.menu.menu_item_image_width || 2048;
        opts.field.data.height = this.menu.menu_item_image_height || 2048;
      }

      this.$refs.imagereplacement.onImageChangeRequested(opts);
    },
    onImageChangeConfirmed(imagePayload) {
      this.payload.image = imagePayload.file;
    },
    validateNameForm() {
      this.$nextTick(() => {
        this.$refs['name-form'].validate();
      });
    },
    fetchData() {
      this.loading = true;

      const promises = [
        this.$api.retrieveMenu(this.menuId).then((response) => {
          this.menu = response.data;
        }),
        this.$store.dispatch('LIST_MENU_CATEGORIES', this.menuId).then(() => {
          this.preselectCategoryFromQueryParams();
        }),
        this.$store
          .dispatch('LIST_MENU_ITEMS', this.menuId)
          .then(this.fetchStoresForEditedMenuItem),
        this.$store.dispatch('LIST_MENU_ADDITIVES', this.menuId),
      ];

      return Promise.all(promises).finally(() => (this.loading = false));
    },
    initializePayloadFromEditedMenuItem() {
      if (this.inEditMode) {
        if (this.payload._assigned) {
          return;
        }
        this.payload = Object.assign({_assigned: true}, this.editedMenuItem);

        this.dietaryChoices = [];
        if (this.payload.is_vegan) {
          this.dietaryChoices.push(VEGETARIAN);
          this.dietaryChoices.push(VEGAN);
        } else if (this.payload.is_vegetarian) {
          this.dietaryChoices.push(VEGETARIAN);
        }
        if (this.payload.is_gluten_free) {
          this.dietaryChoices.push(GLUTEN_FREE);
        }
        if (this.payload.is_lactose_free) {
          this.dietaryChoices.push(LACTOSE_FREE);
        }
      }
    },
    preselectCategoryFromQueryParams() {
      if (!this.$route.query || !this.$route.query.category) {
        return;
      }
      const category = this.menuCategories.filter(
        (cat) => cat.id == this.$route.query.category
      )[0];
      if (category) {
        this.payload.category = category;
      }
    },
    goBack() {
      this.$router.go(-1);
    },
    save() {
      const variantsWidget = this.$refs.variants;
      if (variantsWidget) {
        // Ensure input changes are accepted without pressing Enter or
        // blurring manually.
        variantsWidget.blurAllInputs();
      }

      this.$nextTick(() => {
        if (!this.formIsValid) {
          return;
        }

        this.rememberLastHardRangeValues();

        if (this.inCreateMode) {
          this.createMenuItem(this.payload);
        } else if (this.inEditMode) {
          this.updateMenuItem(this.payload);
        }
      });
    },
    createMenuItem(payload) {
      return this.$api
        .createMenuItem(this.menuId, payload)
        .then(() => {
          this.$router.push({
            name: 'menuitems',
            params: {
              menuId: this.menuId,
            },
            query: {category: payload.category.id},
          });
        })
        .finally(() => {
          this.$store.dispatch('LIST_MENU_CATEGORIES', this.menuId);
          this.$store.dispatch('LIST_MENU_ITEMS', this.menuId);
          this.$store.dispatch('LIST_MENU_ADDITIVES', this.menuId);
        });
    },
    updateMenuItem(menuItem) {
      return this.$api
        .updateMenuItem(menuItem.id, menuItem)
        .catch((error) => {
          console.error(error);
          this.$bus.$emit('alert-error', {
            message: 'genericFailFeedback',
            translate: true,
          });
        })
        .then(() => {
          this.$router.push({name: 'menuitems', params: {menuId: this.menuId}});
        })
        .finally(() => {
          this.$store.dispatch('LIST_MENU_ITEMS', this.menuId);
        });
    },
    /**-----------------------------------------------------------------
     * Stores related methods below
     * ---------------------------------------------------------------*/
    subtractOneDayFromDate: subtractOneDayFromDate,
    fetchStoresForEditedMenuItem() {
      this.loadingStores = true;
      return this.$api
        .listStoresForMenuItem(this.editedMenuItem.id)
        .then((response) => {
          this.stores = response.data;
        })
        .finally(() => (this.loadingStores = false));
    },
    /**
     * Fetch edited MenuItem and ensure it is replaced in the $store list.
     */
    fetchEditedMenuItem() {
      return this.$store.dispatch('RETRIEVE_MENU_ITEM', this.editedMenuItem.id);
    },
    debouncedFetchEditedMenuItem: debounce(function () {
      this.fetchEditedMenuItem({indicator: false});
    }, 300),
    goToStore(store) {
      this.$router.push({
        name: 'storemenu',
        params: {storeId: store.id},
      });
    },
    clearStoresSearch() {
      this.storesSearchText = '';
    },
    confirmEnableEditedMenuItem() {
      const confirmed = confirm(this.$t('menuItemIsDisabledConfirm'));
      if (!confirmed) {
        return;
      }

      const payload = Object.assign({}, this.editedMenuItem);
      payload.is_enabled = true;

      this.$api.updateMenuItem(this.editedMenuItem.id, payload).then(() => {
        this.$store.dispatch('LIST_MENU_ITEMS', this.menuId);
      });
    },
    activateInStore(store) {
      const vm = this;
      const storePayload = Object.assign({}, store);

      // Frontend update
      store.menu_item_active = true;
      store.deactivated_until = null;

      // Backend update
      this.activateMenuItemOnStores(
        this.editedMenuItem,
        [storePayload],
        vm.debouncedFetchEditedMenuItem
      );
    },
    deactivateInStore(store, until = null) {
      const vm = this;
      const toggleBusy = false;

      const menuItemPayload = Object.assign({}, this.editedMenuItem);
      menuItemPayload.until = until;

      const storePayload = Object.assign({}, store);

      // Frontend update
      store.menu_item_active = false;
      if (until) {
        // Add one day to mimic what the backend does, since the display
        // will subtract one day before showing the value.
        const untilPlusOneString = new dayjs(until)
          .add(1, 'day')
          .format('YYYY-MM-DD');
        store.deactivated_until = untilPlusOneString;
      } else {
        store.deactivated_until = null;
      }

      // Backend update
      this.deactivateMenuItemOnStores(
        menuItemPayload,
        [storePayload],
        vm.debouncedFetchEditedMenuItem,
        toggleBusy,
        until
      );
    },
    deactivateInStoreUntilTomorrow(store) {
      const todayString = new dayjs().format('YYYY-MM-DD');
      return this.deactivateInStore(store, todayString);
    },
    openDatePickerForStore(event, store) {
      this.deactivationUntilMenuPositionX = event.clientX;
      this.deactivationUntilMenuPositionY = event.clientY;

      this.deactivationUntilStore = store;

      // Initialize with existing date value or keep the one that was
      // last used. This helps to assign the same date value to multiple
      // MenuItems quickly.
      if (!!store.deactivated_until) {
        this.deactivationUntilDate = store.deactivated_until;
      }

      this.showDeactivationUntilMenu = true;
    },
    onDeactivationUntilDateChosen(store, untilDateString) {
      const vm = this;
      const toggleBusy = false;

      this.deactivateMenuItemOnStores(
        this.editedMenuItem,
        [store],
        vm.fetchStoresForEditedMenuItem,
        toggleBusy,
        untilDateString
      );

      this.onDeactivationUntilDateCancelled();
    },
    onDeactivationUntilDateCancelled() {
      this.deactivationUntilStore = null;
      this.showDeactivationUntilMenu = false;
    },
  },
};
</script>

<style scoped>
.desaturated {
  filter: saturate(0%);
  opacity: 0.66;
}

/* https://codepen.io/lloeki/pen/Dvkqw */
.background-checker-pattern {
  background-repeat: repeat;
  background-size: 16px 16px;
  background-position: 0 0, 8px 8px;
  background-image: linear-gradient(
      45deg,
      #d6d6d6 25%,
      transparent 25%,
      transparent 75%,
      #d6d6d6 75%,
      #d6d6d6
    ),
    linear-gradient(
      45deg,
      #d6d6d6 25%,
      transparent 25%,
      transparent 75%,
      #d6d6d6 75%,
      #d6d6d6
    );
}

.menu-item-image-box {
  background: rgba(0, 0, 0, 0.03);
}
.menu-item-image-box.has-image {
  background: transparent;
}
.menu-item-image-box.border {
  border: 2px dashed rgba(0, 0, 0, 0.24);
  border-radius: 8px;
}
.menu-item-image-box.border:hover,
.menu-item-image-box.border.dragging {
  border: 2px dashed rgba(0, 0, 0, 0.56);
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.05);
}

/**
 * drag and dragend events would otherwise be triggered for each,
 * causing problems with the drag-and-drop preview and/or flickering.
 */
.ignore-mouse-events-on-child-elements * {
  pointer-events: none;
}

.v-image.v-image--left >>> .v-image__image.v-image__image--contain {
  background-position: left center !important;
}

.btn-state--dropdown-icon {
  opacity: 0;
}
.store-row:hover .btn-state--dropdown-icon {
  opacity: 1;
}
</style>



// WEBPACK FOOTER //
// src/pages/MenuItemCreateEdit.vue